import { Component, OnInit, OnDestroy, Input, ElementRef, ViewChild, ContentChild, Output, EventEmitter } from '@angular/core';

import { Subscription, of } from 'rxjs';
import { DndDropEvent } from 'ngx-drag-drop';
import { MessageService } from 'primeng/api';

import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';
import { LayoutBlock } from '../../../../Common/PageBuilder/models/layout-block.model';
import { curatedMentionListBlockType } from '../../models/briefings-block-types.model';
import { focusManagementHelper } from '../../../../Common/Misc/helpers/focus/focus-management.helper';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { CuratedMentionListDisplayElementModel } from '../../models/curated-mention-list-display-element.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { BriefingsService } from '../../services/briefings.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { TextInputDialogComponent } from '../../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '../../../../Common/Dialogs/models/text-input-dialog.model';

import { SortToolComponent } from '../SortTool/sort-tool.component';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { BriefingBlockTypeEnum } from '../../enums/briefing-block-type.enum';


@Component({
    selector: 'agpr-sort-tool-right-sidebar',
    templateUrl: './sort-tool-right-sidebar.component.html',
    styleUrls: ['./sort-tool-right-sidebar.component.scss'],
    standalone: false
})
export class SortToolRightSidebarComponent implements OnInit, OnDestroy {

    // offset the move counter from the mouse position
    readonly dragMoveCounterOffset: any = {
        x: -12, // moves it right 12 pixels
        y: -12 // moves it down 12 pixels
    };
    readonly buttonTypes = ButtonTypesEnum;
    readonly curatedBlockKey: string = curatedMentionListBlockType.key;
    readonly blockListClassName: string = 'sortToolRightSidebarList';
    readonly blockListItemMoveUpClassName: string = 'sortToolRightSidebarListItemMoveUp';
    readonly blockListItemMoveDownClassName: string = 'sortToolRightSidebarListItemMoveDown';
    readonly briefingBlockTypes = BriefingBlockTypeEnum;

    @Input() public id: string;
    @Input() public sortTool: SortToolComponent;
    @Input() draggingMentions: boolean;

    @Output() selectBlockEditTab = new EventEmitter<void>();

    @ViewChild('counter') counter: ElementRef;

    private subscriptions: Subscription[] = [];

    blocks: (BlockModel | LayoutBlock)[];
    loading: boolean = false;
    selectedBlock: BlockModel;

    briefingCuratedMentions: Mention[];
    draggingBlock: BlockModel;
    nextFocusListItemIndex: number = -1;
    isMoveUp: boolean = false;
    isMoveDown: boolean = false;


    constructor(
        private coreDialogService: CoreDialogService,
        private briefingsService: BriefingsService,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) { }

    ngOnInit() {
        this.subscriptions.push(
            this.briefingsService.getBlocks().subscribe(
                (blocks) => {
                    this.blocks = blocks || [];

                    for (const block of this.blocks) {
                        // Cache the value for use in the template
                        (block as LayoutBlock).isLayoutBlock = block.isLayoutBlockType();
                    }

                    this.restoreFocus();
                }
            ),
            this.briefingsService.getSelectedBlock().subscribe(
                (block) => {
                    this.selectedBlock = block;
                }
            ),
            this.briefingsService.getCurrentBriefingMentions().subscribe(
                mentions => this.briefingCuratedMentions = mentions
            )
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
    }

    /**
     * Sets the selected block
     */
    selectBlock(block: BlockModel, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        // Can't select layout blocks
        if (block.isLayoutBlockType() || block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
            return;
        }

        this.briefingsService.selectBlock(block);
    }

    /**
     * Gets the index of a specific block
     * @throws Error if the block is not in the current list of blocks
     */
    private getBlockIndex(block: BlockModel): number {
        const index = this.blocks.indexOf(block);

        if (index < 0) {
            // shouldn't ever happen
            throw new Error('Item not in list');
        }
        return index;
    }

    /**
     * Moves a block up by 1 if it's not already at the top
     */
    onMoveUp(block: BlockModel): void {
        const index = this.getBlockIndex(block);
        const blocks = this.blocks;

        if (index === 0) {
            // already at the top, can't move up more
            return;
        }

        blocks.splice(index, 1);
        blocks.splice(index - 1, 0, block);
        this.nextFocusListItemIndex = index - 1;
        this.isMoveUp = true;

        this.briefingsService.setBlocks(blocks);

    }

    /**
     * Moves a block down by 1 if it's not already at the bottom
     */
    onMoveDown(block: BlockModel): void {
        const index = this.getBlockIndex(block);
        const blocks = this.blocks;

        if (index === blocks.length - 1) {
            // already at the bottom, can't move down more
            return;
        }

        blocks.splice(index, 1);
        blocks.splice(index + 1, 0, block);
        this.nextFocusListItemIndex = index + 1;
        this.isMoveDown = true;

        this.briefingsService.setBlocks(blocks);
    }

    /**
     * Selects a block
     */
    onEdit(block: BlockModel): void {
        this.selectBlock(block);
        this.selectBlockEditTab.emit();
    }

    /**
     * Deletes a block from the briefing
     */
    onDelete(block: BlockModel): void {
        let name;

        if (!block.isLayoutBlockType()) {
            const curatedDisplayElement = block.displayElement as CuratedMentionListDisplayElementModel;
            name = curatedDisplayElement.title;
        } else {
            name = this.localizationService.transform(block.type.label);
        }

        this.coreDialogService.open(ConfirmDialogComponent, {
            inputs: {
                id: 'dataConnector.deleteConnectionConfirmDialog',
                heading: 'PageBuilder.briefings.delete.confirmDialogHeading',
                alertMessage: 'PageBuilder.briefings.delete.confirmDialogAlertMessage',
                alertType: messageSeverityEnum.warning,
                message: this.localizationService.transform('PageBuilder.briefings.delete.confirmDialogAreYouSure', { name: name }),
                okLabel: 'PageBuilder.briefings.delete.confirmDialogConfirm',
                confirmAction: () => {
                    const index = this.getBlockIndex(block);
                    const blocks = this.blocks;

                    blocks.splice(index, 1);
                    this.briefingsService.setBlocks(blocks);
                    return of(true);
                }
            }
        });

    }

    /**
     * Opens the New Curated List dialog for picking a name
     */
    openNewCuratedListDialog(): void {
        const io: TextInputDialogModel = {
            inputs: {
                width: 300,
                maxLength: 256,
                heading: 'Briefing.sortTool.newCuratedList.heading',
                description: 'Briefing.sortTool.newCuratedList.message',
                submit: (curatedBlockName: string) => {
                    this.briefingsService.addCuratedBlock(curatedBlockName);
                    this.briefingsService.updateBlocks();
                    return of(curatedBlockName);
                }
            }
        };

        this.coreDialogService.open(TextInputDialogComponent, io);
    }
    cancelEvent(event: any): boolean {
        if (event instanceof KeyboardEvent) {
            return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
    }

    /**
     * Handles the event when a block is dropped into a new position in the block list
     */
    onDropBlocks(event: DndDropEvent): void {
        if (event.index === undefined || event.index === null) {
            return;
        }
        if (event.dropEffect === 'move') {
            let index = event.index;
            const blocks = this.blocks.slice();
            const indexOfBlock = blocks.indexOf(this.draggingBlock);

            blocks.splice(indexOfBlock, 1);
            if (index > indexOfBlock) {
                index--;
            }
            blocks.splice(index, 0, this.draggingBlock);
            this.briefingsService.setBlocks(blocks);
        }
    }

    /**
     * Handles starting a drag event for blocks
     */
    onDragStartBlocks(block: BlockModel, event: DragEvent): void {
        this.draggingBlock = block;
        this.draggingMentions = false;
    }

    /**
     * Cleans up after a drag has been completed
     *
     * @return void
     */
    public cleanupAfterDragCompleted(event: any): void {
        this.draggingBlock = null;
        setTimeout(() => {
            if (event?.target?.style) {
                event.target.style.pointerEvents = 'unset';
            }
        }, 500);
    }

    /**
     * Handles mentions being dropped onto a different block from the center panel to the right hand panel
     */
    onDropMentionsOnBlock(event: DndDropEvent, block: BlockModel): void {
        if (event.type !== 'mention') {
            return;
        }
        let mentions = this.sortTool.draggingItems;
        const sourceDisplayElement = this.selectedBlock.displayElement as CuratedMentionListDisplayElementModel;
        const targetDisplayElement = block.displayElement as CuratedMentionListDisplayElementModel;

        let numOfDuplicatedMentions = 0;
        mentions.forEach(item => {
            if (targetDisplayElement.mentions?.some(targetItem => targetItem.id === item.mentionId)) {
                numOfDuplicatedMentions ++;
                mentions = mentions.filter(dragMention => dragMention.mentionId !== item.mentionId);
            } else {
                targetDisplayElement.mentions.push(item.convertToApiMention());
            }
        });
        targetDisplayElement.sortMentions();

        if (numOfDuplicatedMentions) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Briefings.sortTool.duplicatedMention.summary'),
                detail: this.localizationService.transform(
                    'Briefings.sortTool.duplicatedMention.details',
                    { 'number': numOfDuplicatedMentions }
                )
            });
        }

        sourceDisplayElement.mentions = sourceDisplayElement.mentions.filter(
            mention => !mentions.find(dragMention => dragMention.mentionId === mention.id)
        );
        sourceDisplayElement.mentionIds = sourceDisplayElement.mentions.map(apiMention => apiMention.id);

        this.draggingMentions = false;
        this.briefingsService.formFieldUpdated();
    }

    reorganizeMentions(): void {
        this.briefingsService.refreshAndReorganizeMentions();
        this.briefingsService.formFieldUpdated();

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Briefings.sortTool.reorganizeMentions.success.heading'),
            detail: this.localizationService.transform('Briefings.sortTool.reorganizeMentions.success.message')
        });
    }

    private restoreFocus(): void {
        requestAnimationFrame(() => {
            if (this.isMoveUp) {
                focusManagementHelper.restoreListFocus(this.blockListClassName, this.nextFocusListItemIndex, this.blockListItemMoveUpClassName + ' > button');
                this.isMoveUp = false;
            }
            if (this.isMoveDown) {
                focusManagementHelper.restoreListFocus(this.blockListClassName, this.nextFocusListItemIndex, this.blockListItemMoveDownClassName + ' > button');
                this.isMoveDown = false;
            }
            this.nextFocusListItemIndex = -1;
        });
    }
}
